import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentTextIcon,
  FolderIcon,
  PhotoIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { blogService } from '../services/blogService';
import { BlogPost, BlogFilters } from '../types/blog';
import CategoryManager from '../components/Blog/CategoryManager';
import ImageManagement from '../components/Blog/ImageManagement';

const BlogManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'posts' | 'categories' | 'images'>('posts');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BlogFilters>({
    status: 'all',
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    page: 1,
    limit: 10,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (activeTab === 'posts') {
      loadPosts();
    }
  }, [filters, activeTab]);

  const loadPosts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading posts with filters:', filters);
      const response = await blogService.getBlogPosts(filters);
      console.log('📊 Blog posts response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Posts data received:', response.data);
        // Handle the nested response format: { success: true, data: { posts: [], ... } }
        const responseData = response.data as any;
        
        // Check if data has the nested structure with success/data
        if (responseData.success && responseData.data) {
          // Nested format: { success: true, data: { posts: [], total, ... } }
          const innerData = responseData.data;
          if (innerData.posts) {
            setPosts(innerData.posts);
            setTotalPages(innerData.totalPages || 1);
            setTotal(innerData.total || 0);
          } else {
            setPosts([]);
            setTotalPages(1);
            setTotal(0);
          }
        } else if (Array.isArray(responseData)) {
          // If data is directly an array of posts
          setPosts(responseData);
          setTotalPages(1);
          setTotal(responseData.length);
        } else if (responseData.posts) {
          // If data has the expected structure directly
          setPosts(responseData.posts);
          setTotalPages(responseData.totalPages || 1);
          setTotal(responseData.total || 0);
        } else {
          console.warn('⚠️ Unexpected data format:', response.data);
          setPosts([]);
          setTotalPages(1);
          setTotal(0);
        }
      } else {
        console.error('❌ API response failed:', response);
        setError(response.error?.message || 'Failed to load blog posts');
        setPosts([]); // Ensure posts is always an array
      }
    } catch (err) {
      console.error('❌ Error loading posts:', err);
      setError('Failed to load blog posts');
      setPosts([]); // Ensure posts is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status: string) => {
    setFilters(prev => ({ ...prev, status: status as any, page: 1 }));
  };

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: sortBy as any,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleQuickAction = async (postId: string, action: 'publish' | 'unpublish' | 'delete') => {
    try {
      let response;
      
      switch (action) {
        case 'publish':
          response = await blogService.publishBlogPost(postId);
          break;
        case 'unpublish':
          response = await blogService.unpublishBlogPost(postId);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this blog post?')) {
            response = await blogService.deleteBlogPost(postId);
          }
          break;
      }
      
      if (response?.success) {
        loadPosts(); // Reload the list
      } else {
        setError(response?.error?.message || `Failed to ${action} post`);
      }
    } catch (err) {
      setError(`Failed to ${action} post`);
      console.error(`Error ${action}ing post:`, err);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'scheduled':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'categories':
        return <CategoryManager />;
      case 'images':
        return <ImageManagement />;
      case 'posts':
      default:
        return renderPostsContent();
    }
  };

  const renderPostsContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600 font-medium">Loading your blog posts...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Enhanced Filters and Search */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search posts by title, content, or tags..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="lg:w-48">
              <select
                value={filters.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200 font-medium"
              >
                <option value="all">📊 All Status</option>
                <option value="published">✅ Published</option>
                <option value="draft">📝 Draft</option>
                <option value="scheduled">⏰ Scheduled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg flex items-center gap-3">
            <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Enhanced Posts Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                <tr>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-colors duration-200"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Title</span>
                      {filters.sortBy === 'title' && (
                        <span className="text-blue-600">
                          {filters.sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Categories
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-colors duration-200"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Last Updated</span>
                      {filters.sortBy === 'updatedAt' && (
                        <span className="text-blue-600">
                          {filters.sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-blue-100 transition-colors duration-200"
                    onClick={() => handleSort('viewCount')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Views</span>
                      {filters.sortBy === 'viewCount' && (
                        <span className="text-blue-600">
                          {filters.sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {posts && posts.length > 0 ? posts.map((post) => (
                  <tr key={post._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                          {post.title}
                        </div>
                        <div className="text-sm text-gray-500 font-mono">
                          /{post.slug}
                        </div>
                        {post.content && (
                          <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <CheckCircleIcon className="w-3 h-3" />
                            {post.content.length} characters
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`${getStatusBadge(post.status)} shadow-sm`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {post.categories.slice(0, 2).map((category) => (
                          <span
                            key={category._id}
                            className="px-3 py-1 text-xs bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full font-medium"
                          >
                            {category.name}
                          </span>
                        ))}
                        {post.categories.length > 2 && (
                          <span className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full font-medium">
                            +{post.categories.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                      {formatDate(post.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-semibold">
                      {post.viewCount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-3">
                        <Link
                          to={`/blog/editor/${post._id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                        >
                          Edit
                        </Link>
                        {post.status === 'draft' ? (
                          <button
                            onClick={() => handleQuickAction(post._id, 'publish')}
                            className="text-green-600 hover:text-green-800 font-medium hover:underline transition-colors"
                          >
                            Publish
                          </button>
                        ) : (
                          <button
                            onClick={() => handleQuickAction(post._id, 'unpublish')}
                            className="text-amber-600 hover:text-amber-800 font-medium hover:underline transition-colors"
                          >
                            Unpublish
                          </button>
                        )}
                        <button
                          onClick={() => handleQuickAction(post._id, 'delete')}
                          className="text-red-600 hover:text-red-800 font-medium hover:underline transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-2xl text-gray-400">📝</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {loading ? 'Loading posts...' : 'No blog posts found'}
                          </h3>
                          <p className="text-gray-500">
                            {loading ? 'Please wait while we fetch your content...' : 'Get started by creating your first blog post.'}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination */}
          {totalPages > 1 && (
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(Math.max(1, (filters.page || 1) - 1))}
                  disabled={(filters.page || 1) <= 1}
                  className="relative inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-all duration-200"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, (filters.page || 1) + 1))}
                  disabled={(filters.page || 1) >= totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border-2 border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-all duration-200"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 font-medium">
                    Showing{' '}
                    <span className="font-bold text-blue-600">
                      {((filters.page || 1) - 1) * (filters.limit || 10) + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-bold text-blue-600">
                      {Math.min((filters.page || 1) * (filters.limit || 10), total)}
                    </span>{' '}
                    of <span className="font-bold text-blue-600">{total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ${
                          page === (filters.page || 1)
                            ? 'z-10 bg-gradient-to-r from-blue-500 to-indigo-600 border-blue-500 text-white shadow-lg'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-blue-50 hover:border-blue-300'
                        } ${page === 1 ? 'rounded-l-xl' : ''} ${page === totalPages ? 'rounded-r-xl' : ''}`}
                      >
                        {page}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Empty State */}
        {posts.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <DocumentTextIcon className="w-12 h-12 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No blog posts yet</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Get started by creating your first blog post. Share your thoughts, insights, and stories with your audience.
              </p>
              <Link
                to="/blog/editor"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl gap-2"
              >
                <PlusIcon className="w-5 h-5" />
                Create Your First Post
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (activeTab === 'posts' && loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6 space-y-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Blog Management
                </h1>
                <p className="text-gray-600 text-lg">
                  Manage your blog posts, categories, and media content
                </p>
              </div>
            </div>
          </div>
          {activeTab === 'posts' && (
            <Link
              to="/blog/editor"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Create New Post
            </Link>
          )}
        </div>
      </div>

      {/* Enhanced Navigation Tabs */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50">
          <nav className="flex">
            {[
              { id: 'posts', label: 'Posts', icon: DocumentTextIcon, count: posts.length },
              { id: 'categories', label: 'Categories', icon: FolderIcon },
              { id: 'images', label: 'Images', icon: PhotoIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 py-4 px-6 font-semibold text-sm transition-all duration-200 border-b-3 flex items-center justify-center gap-3 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-white shadow-sm'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.id 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-8">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default BlogManagement;